'use client';

import React from 'react';

import { useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState, OverlayTriggerState } from 'react-stately';

import { DismissButton, Overlay, usePopover, Placement } from 'react-aria';
import { Pressable } from 'bwax-ui/components/Button';

import styleClasses from './PopoverTrigger.module.css';
import clsx from 'clsx';

// 简单类型定义，不影响原有代码结构
type PopoverProps = {
  className?: string;
  offset?: number;
  zIndex?: number;
  withArrow?: boolean;
  children: React.ReactNode | Function;
  state: OverlayTriggerState;
  triggerRef?: React.RefObject<any>;
  placement?: Placement;
  [key: string]: any;
};

type PopoverTriggerProps = {
  className?: string;
  popoverClassName?: string;
  content: React.ReactElement | Function;
  onTriggerPress?: Function;
  children: React.ReactNode;
  placement?: Placement;
  [key: string]: any;
};

/**

'bottom'
  | 'bottom left'
  | 'bottom right'
  | 'bottom start'
  | 'bottom end'
  | 'top'
  | 'top left'
  | 'top right'
  | 'top start'
  | 'top end'
  | 'left'
  | 'left top'
  | 'left bottom'
  | 'start'
  | 'start top'
  | 'start bottom'
  | 'right'
  | 'right top'
  | 'right bottom'
  | 'end'
  | 'end top'
  | 'end bottom'

 */

// 与原始JS版本保持一致，只添加类型注解
export function Popover({ 
    className, offset = 8, zIndex, withArrow = true, children, 
    state, placement = 'bottom' as Placement,
    ...props 
}: PopoverProps) {
    
    const popoverRef = React.useRef(null);

    // 完全按照原JS版本的方式调用
    // @ts-ignore - 忽略类型错误
    const { popoverProps, underlayProps, arrowProps } = usePopover({
        ...props, offset, popoverRef, placement
    }, state);

    function renderContent() {
        if(typeof(children) == "function") {
            return children({ close: state.close, overlayProps: {} })
        } else {
            return children
        }
    }

    return (
        <Overlay>
            <div {...underlayProps} className={styleClasses["lc-popover-underlay"]} />
            <div
                {...popoverProps}
                style={{
                    ...popoverProps.style,
                    zIndex: zIndex || 99999,
                }}
                ref={popoverRef}
                className={clsx("lc-base lc-with-bg-2", className, styleClasses["lc-popover-2"])}
            >
                {/* {withArrow ? <svg {...arrowProps} className="arrow" data-placement={placement}>
                    <path d="M0 0,L6 6,L12 0" />
                </svg> : null} */}
                <DismissButton onDismiss={state.close} />
                    { renderContent() }
                <DismissButton onDismiss={state.close} />
            </div>
        </Overlay>
    );
}

export default function PopoverTrigger({ placement = 'bottom' as Placement, className = "", popoverClassName = "", content, onTriggerPress = (_:any) => {}, children, ...props }: PopoverTriggerProps) {
    const ref = React.useRef(null);
    const state = useOverlayTriggerState(props);
    const { triggerProps, overlayProps } = useOverlayTrigger(
        { type: 'dialog' }, state, ref
    );

    const { onPress, ...otherTriggerProps } = triggerProps;

    function renderContent() {
        if(typeof(content) == "function") {
            return content({ close: state.close, overlayProps })
        } else {
            return React.cloneElement(content, overlayProps)
        }
    }

    return (
        <>
            <Pressable {...otherTriggerProps} onPress={(_:any) => {
                if(onTriggerPress) { onTriggerPress() }
                // @ts-ignore - 忽略TS错误，保持与JS版本一致
                onPress && onPress();
            }} ref={ref}>{React.isValidElement(children) ? children : <div>{children}</div>}</Pressable>
            {state.isOpen &&
                (
                    <Popover 
                        className={popoverClassName} 
                        placement={placement} 
                        {...props} 
                        triggerRef={ref} 
                        state={state}
                    >
                        {renderContent()}
                    </Popover>
                )}
        </>
    );
}

export function create(props: { 
    children: React.ReactNode, 
    className?: string, 
    content: React.ReactElement, 
    key?: string,
    placement?: Placement 
}) {
    const { children, className, content, key, placement } = props;

    return (
        <PopoverTrigger 
            content={content} 
            className={className} 
            key={key}
            placement={placement}
        >
            { children }
        </PopoverTrigger>
    )
}